import { useEffect, useRef, useState } from "react";
import { Loader2, PlayCircle, Trash2, Upload, Eye, CheckCircle2, SkipForward, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

const CHUNK = 900 * 1024;  // under the 1 MB body limit most proxies / ingresses default to
const RETRIES = 3;

// Owner uploads / replaces the walkthrough video behind the toolbar's "Watch Video" tab (chunked, so big MP4s go through).
// Who has watched the walkthrough: counts + the latest viewers.
export const IntroVideoStats = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { api.adminIntroVideoStats().then(setSt).catch(() => setSt({ error: true })); }, []);
  if (!st) return <div className="text-[11px] text-[#9aa0a8]">Loading stats…</div>;
  if (st.error) return <div className="text-[11px] text-[#e5484d]">Couldn't load watch stats</div>;
  const pct = (n) => (st.customers ? Math.round((n / st.customers) * 100) : 0);
  const Card = ({ icon: Icon, label, n, tone, testId }) => (
    <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] text-[#9aa0a8]"><Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}</div>
      <div className="mt-0.5 text-[20px] font-semibold text-white">{n} <span className="text-[11px] font-normal text-[#6b7079]">/ {st.customers} · {pct(n)}%</span></div>
    </div>
  );
  const label = { watched: "Watched", partial: "Partly watched", skipped: "Skipped" };
  const tone = { watched: "text-[#3ecf8e]", partial: "text-[#f5a524]", skipped: "text-[#9aa0a8]" };
  return (
    <div className="space-y-3" data-testid="admin-intro-video-stats">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Card icon={CheckCircle2} label="Watched (≥ 90%)" n={st.watched} tone="text-[#3ecf8e]" testId="video-stat-watched" />
        <Card icon={Eye} label="Partly watched" n={st.partial} tone="text-[#f5a524]" testId="video-stat-partial" />
        <Card icon={SkipForward} label="Skipped" n={st.skipped} tone="text-[#9aa0a8]" testId="video-stat-skipped" />
        <Card icon={Clock} label="Haven't opened it" n={st.not_yet} tone="text-[#6b7079]" testId="video-stat-notyet" />
      </div>
      <div className="text-[11px] text-[#9aa0a8]">{st.dismissed} customer{st.dismissed === 1 ? "" : "s"} ticked “Don't show this again”.</div>
      {st.recent.length > 0 && (
        <div className="max-h-64 overflow-y-auto rounded-lg border border-white/10" data-testid="video-stat-list">
          {st.recent.map((v) => (
            <div key={v.user_id} className="flex items-center justify-between gap-3 border-b border-white/5 px-3 py-1.5 text-[11.5px] last:border-0">
              <span className="min-w-0 truncate text-[#e8e9ec]">{v.shop_name || v.email}<span className="text-[#6b7079]"> · {v.email}</span></span>
              <span className={`flex-shrink-0 font-semibold ${tone[v.status] || ""}`}>{label[v.status] || v.status}{v.status !== "skipped" ? ` · ${Math.round(v.pct)}%` : ""}<span className="ml-2 font-normal text-[#6b7079]">{new Date(v.last_at).toLocaleDateString()}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const IntroVideoControl = () => {
  const [info, setInfo] = useState(null);
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const file = useRef(null);
  const load = () => api.introVideo().then((i) => { setInfo(i); setTitle(i.title || ""); }).catch(() => setInfo({ available: false }));
  useEffect(() => { load(); }, []);
  const upload = async (f) => {
    if (!f) return;
    if (!/\.(mp4|m4v|mov|webm)$/i.test(f.name)) return toast.error("Use an MP4 (or MOV / WebM) file");
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const total = Math.max(1, Math.ceil(f.size / CHUNK));
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const blob = f.slice(i * CHUNK, Math.min(f.size, (i + 1) * CHUNK));
        for (let attempt = 1; ; attempt++) {  // a dropped connection mid-upload just retries that piece
          try { await api.adminIntroVideoChunk(id, i, blob, i === total - 1, f.name, title); break; }
          catch (e) { if (attempt >= RETRIES || e.response) throw e; await new Promise((r) => setTimeout(r, 800 * attempt)); }
        }
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success("Video uploaded — customers see it under Watch Video");
      await load();
    } catch (e) { toast.error(e.response ? apiError(e) : "Upload interrupted (network) — check your connection and try again; the upload resumes from scratch."); } finally { setProgress(null); if (file.current) file.current.value = ""; }
  };
  const remove = async () => { try { await api.adminIntroVideoDelete(); toast.success("Video removed"); await load(); } catch (e) { toast.error(apiError(e)); } };
  const saveTitle = async () => { try { await api.adminIntroVideoTitle(title); toast.success("Title saved"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-2 rounded-lg border border-white/10 bg-black/20 p-3" data-testid="admin-intro-video">
      <div className="flex items-center gap-2 text-[12px] font-semibold text-white"><PlayCircle className="h-4 w-4 text-[#ff8a8e]" /> Watch Video (toolbar)</div>
      <div className="text-[11px] text-[#9aa0a8]" data-testid="admin-intro-video-status">
        {info === null ? "Loading…" : info.available ? `Live · ${(info.size / 1048576).toFixed(1)} MB · ${info.filename}${info.updated_at ? ` · ${new Date(info.updated_at).toLocaleDateString()}` : ""}` : "No video yet — customers see “Video coming soon”."}
      </div>
      <div className="flex items-center gap-2">
        <input data-testid="admin-intro-video-title" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} placeholder="Title shown above the player" className="field h-8 flex-1 py-0 text-[12px]" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={file} data-testid="admin-intro-video-file" type="file" accept=".mp4,.m4v,.mov,.webm,video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <button data-testid="admin-intro-video-upload" disabled={progress !== null} onClick={() => file.current?.click()} className="btn flex items-center gap-1.5 text-[11px] disabled:opacity-50">
          {progress !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {progress !== null ? `Uploading ${progress}%` : info?.available ? "Replace video" : "Upload MP4"}
        </button>
        {info?.available && <button data-testid="admin-intro-video-delete" onClick={remove} className="glass-btn flex items-center gap-1 px-2.5 py-1 text-[11px] text-[#e5484d]"><Trash2 className="h-3.5 w-3.5" /> Remove</button>}
      </div>
    </div>
  );
};
